// features/tutorials/components/LessonView.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, CheckCircle, Lightbulb, Clock, ArrowLeft, Check, Maximize2, Minimize2, Menu } from 'lucide-react';
import { StepNavigator } from './StepNavigator';
import { StepContent } from './StepContent';
import type { TutorialLesson, TutorialModule, LearningProgress } from '../types';

interface LessonViewProps {
    lesson: TutorialLesson;
    onClose: () => void;
    onComplete: (lessonId: string) => void;
    progress: LearningProgress;
    modules: TutorialModule[];
    onUpdateProgress: (progress: LearningProgress) => void;
}

export const LessonView: React.FC<LessonViewProps> = ({
    lesson,
    onClose,
    onComplete,
    progress,
    modules,
    onUpdateProgress,
}) => {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [completedSteps, setCompletedSteps] = useState<number[]>([]);
    const [showCompletion, setShowCompletion] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [showMobileNav, setShowMobileNav] = useState(false);

    const currentModule = modules.find((m: TutorialModule) =>
        m.lessons.some((l: TutorialLesson) => l.id === lesson.id)
    );
    const isLastStep = currentStepIndex === lesson.steps.length - 1;
    const currentStep = lesson.steps[currentStepIndex];
    const isFirstStep = currentStepIndex === 0;

    // Initialize completed steps from progress
    useEffect(() => {
        const savedSteps = progress.completedSteps?.[lesson.id] || [];
        setCompletedSteps(savedSteps);
        if (savedSteps.length > 0 && savedSteps.length < lesson.steps.length) {
            setCurrentStepIndex(Math.min(Math.max(...savedSteps) + 1, lesson.steps.length - 1));
        }
    }, [lesson.id, progress.completedSteps]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Don't handle if user is typing in an input
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }

            if (e.key === 'ArrowRight' && !isLastStep) {
                e.preventDefault();
                handleNextStep();
            } else if (e.key === 'ArrowLeft' && !isFirstStep) {
                e.preventDefault();
                handlePrevStep();
            } else if (e.key === 'Escape') {
                if (showMobileNav) {
                    setShowMobileNav(false);
                } else {
                    onClose();
                }
            } else if (e.key === 'Enter' && isLastStep && !isAllStepsCompleted()) {
                handleMarkComplete();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentStepIndex, isLastStep, isFirstStep, showMobileNav]);

    const isAllStepsCompleted = useCallback(() => {
        return lesson.steps.every((_, index: number) => completedSteps.includes(index));
    }, [lesson.steps, completedSteps]);

    const handleStepComplete = useCallback(() => {
        if (!completedSteps.includes(currentStepIndex)) {
            const newCompletedSteps = [...completedSteps, currentStepIndex];
            setCompletedSteps(newCompletedSteps);

            // Save progress
            const updatedProgress: LearningProgress = {
                ...progress,
                completedSteps: {
                    ...progress.completedSteps,
                    [lesson.id]: newCompletedSteps
                },
                lastViewedStep: {
                    ...progress.lastViewedStep,
                    [lesson.id]: currentStepIndex,
                },
                lastActivityDate: new Date().toISOString().split('T')[0],
            };
            onUpdateProgress(updatedProgress);
        }

        // If all steps complete, mark lesson as complete
        if (isLastStep || (completedSteps.length + 1 === lesson.steps.length)) {
            setShowCompletion(true);
            onComplete(lesson.id);
            setTimeout(() => {
                setShowCompletion(false);
            }, 3000);
        }
    }, [currentStepIndex, completedSteps, lesson.id, lesson.steps.length, isLastStep, onComplete, progress, onUpdateProgress]);

    const handleMarkComplete = () => {
        if (!isAllStepsCompleted()) {
            // Mark all remaining steps as complete
            const allSteps = lesson.steps.map((_, index: number) => index);
            const newCompletedSteps = [...new Set([...completedSteps, ...allSteps])];
            setCompletedSteps(newCompletedSteps);

            const updatedProgress: LearningProgress = {
                ...progress,
                completedSteps: {
                    ...progress.completedSteps,
                    [lesson.id]: newCompletedSteps
                },
                lastViewedStep: {
                    ...progress.lastViewedStep,
                    [lesson.id]: lesson.steps.length - 1,
                },
                lastActivityDate: new Date().toISOString().split('T')[0],
            };
            onUpdateProgress(updatedProgress);

            setShowCompletion(true);
            onComplete(lesson.id);
            setTimeout(() => setShowCompletion(false), 3000);
        }
    };

    const handleNextStep = () => {
        if (currentStepIndex < lesson.steps.length - 1) {
            // Mark current step as complete when moving forward
            if (!completedSteps.includes(currentStepIndex)) {
                handleStepComplete();
            }
            setCurrentStepIndex(prev => prev + 1);
        }
    };

    const handlePrevStep = () => {
        if (currentStepIndex > 0) {
            setCurrentStepIndex(prev => prev - 1);
        }
    };

    const handleStepClick = (index: number) => {
        setCurrentStepIndex(index);
        setShowMobileNav(false);
    };

    const progressPercent = (completedSteps.length / lesson.steps.length) * 100;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-white dark:bg-slate-900 overflow-hidden"
        >
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-4">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={onClose}
                                className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                aria-label="Close lesson"
                            >
                                <X className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                            </motion.button>

                            <div>
                                <div className="flex items-center space-x-2 text-sm text-slate-500 dark:text-slate-400">
                                    <span>{currentModule?.title}</span>
                                    <ChevronRight className="w-3 h-3" />
                                    <span className="text-slate-900 dark:text-white font-medium truncate max-w-xs">
                                        {lesson.title}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center space-x-4">
                            <div className="hidden sm:flex items-center space-x-4">
                                <span className="flex items-center text-sm text-slate-500 dark:text-slate-400">
                                    <Clock className="w-4 h-4 mr-1" />
                                    {lesson.duration}
                                </span>
                                <span className="text-sm text-slate-500 dark:text-slate-400">
                                    Step {currentStepIndex + 1} of {lesson.steps.length}
                                </span>
                            </div>

                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                                className="hidden lg:flex p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
                            >
                                {isSidebarOpen ? (
                                    <Minimize2 className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                                ) : (
                                    <Maximize2 className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                                )}
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setShowMobileNav(true)}
                                className="lg:hidden p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                                aria-label="Show steps"
                            >
                                <Menu className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                            </motion.button>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <motion.div
                            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                            initial={{ width: `${(completedSteps.filter(s => s <= currentStepIndex).length / lesson.steps.length) * 100}%` }}
                            animate={{ width: `${progressPercent}%` }}
                            transition={{ duration: 0.5, ease: 'easeInOut' }}
                        />
                    </div>
                </div>
            </div>

            <div className="flex h-[calc(100vh-89px)]">
                {/* Sidebar Navigation - Desktop */}
                <AnimatePresence>
                    {isSidebarOpen && (
                        <motion.div
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 280, opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="hidden lg:block border-r border-slate-200 dark:border-slate-700 overflow-y-auto bg-slate-50/50 dark:bg-slate-800/50"
                        >
                            <StepNavigator
                                steps={lesson.steps}
                                currentStepIndex={currentStepIndex}
                                completedSteps={completedSteps}
                                onStepClick={handleStepClick}
                            />
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Mobile Navigation Overlay */}
                <AnimatePresence>
                    {showMobileNav && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 lg:hidden"
                        >
                            <div
                                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                                onClick={() => setShowMobileNav(false)}
                            />
                            <motion.div
                                initial={{ x: '-100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '-100%' }}
                                transition={{ type: 'spring', damping: 30 }}
                                className="absolute left-0 top-0 bottom-0 w-80 bg-white dark:bg-slate-900 shadow-2xl overflow-y-auto"
                            >
                                <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between">
                                    <h3 className="font-semibold text-slate-900 dark:text-white">Lesson Steps</h3>
                                    <button
                                        onClick={() => setShowMobileNav(false)}
                                        className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                                    >
                                        <X className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                                    </button>
                                </div>
                                <StepNavigator
                                    steps={lesson.steps}
                                    currentStepIndex={currentStepIndex}
                                    completedSteps={completedSteps}
                                    onStepClick={handleStepClick}
                                />
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Main Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentStepIndex}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                <StepContent step={currentStep} />
                            </motion.div>
                        </AnimatePresence>

                        {/* Tips Section */}
                        {lesson.tips && lesson.tips.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="mt-8 p-6 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-200 dark:border-amber-700"
                            >
                                <div className="flex items-center space-x-2 mb-3">
                                    <Lightbulb className="w-5 h-5 text-amber-500" />
                                    <h3 className="font-semibold text-amber-900 dark:text-amber-200">Pro Tips</h3>
                                </div>
                                <ul className="space-y-2">
                                    {lesson.tips.map((tip: string, index: number) => (
                                        <li key={index} className="flex items-start space-x-2 text-sm text-amber-800 dark:text-amber-300">
                                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                                            <span>{tip}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        )}

                        {/* Related Features Section */}
                        {lesson.relatedFeatures && lesson.relatedFeatures.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                                className="mt-8 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-200 dark:border-blue-700"
                            >
                                <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-3">
                                    Related Features
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {lesson.relatedFeatures.map((feature: string, index: number) => (
                                        <span
                                            key={index}
                                            className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300"
                                        >
                                            {feature}
                                        </span>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {/* Navigation Buttons */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="mt-8 flex items-center justify-between"
                        >
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handlePrevStep}
                                disabled={isFirstStep}
                                className="flex items-center space-x-2 px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                <span>Previous</span>
                            </motion.button>

                            <div className="flex items-center space-x-3">
                                {!isLastStep ? (
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handleNextStep}
                                        className="flex items-center space-x-2 px-6 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium shadow-lg hover:shadow-xl transition-shadow"
                                    >
                                        <span>Next Step</span>
                                        <ChevronRight className="w-4 h-4" />
                                    </motion.button>
                                ) : (
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handleMarkComplete}
                                        disabled={isAllStepsCompleted()}
                                        className="flex items-center space-x-2 px-6 py-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium shadow-lg hover:shadow-xl transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Check className="w-4 h-4" />
                                        <span>Complete Lesson</span>
                                    </motion.button>
                                )}
                            </div>
                        </motion.div>

                        {/* Completion Animation */}
                        <AnimatePresence>
                            {showCompletion && (
                                <motion.div
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0, opacity: 0 }}
                                    className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
                                >
                                    <motion.div
                                        animate={{
                                            scale: [1, 1.2, 1],
                                            rotate: [0, 10, -10, 0]
                                        }}
                                        transition={{ duration: 0.5 }}
                                        className="bg-white dark:bg-slate-800 rounded-full p-8 shadow-2xl"
                                    >
                                        <CheckCircle className="w-16 h-16 text-green-500" />
                                    </motion.div>
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.3 }}
                                        className="absolute mt-32 text-center"
                                    >
                                        <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                                            Lesson Complete! 🎉
                                        </h3>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};